// Base utilities for API calls
export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(endpoint, {
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`API Error: ${response.status} ${error}`);
  }

  return response.json();
}

export function apiGet<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: "GET" });
}

export function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: "POST",
    body: data ? JSON.stringify(data) : undefined,
  });
}

export function apiPatch<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: "PATCH",
    body: data ? JSON.stringify(data) : undefined,
  });
}

export function apiDelete<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: "DELETE" });
}

// lib/api/base.ts
export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export type Route<R, B = undefined> = {
  method: HttpMethod;
  url: string;
  // phantom types for inference
  __resp?: R;
  __body?: B;
};

export function get<R>(url: string): Route<R, undefined> {
  return { method: "GET", url };
}
export function post<R, B>(url: string): Route<R, B> {
  return { method: "POST", url };
}
export function patch<R, B>(url: string): Route<R, B> {
  return { method: "PATCH", url };
}
export function put<R, B>(url: string): Route<R, B> {
  return { method: "PUT", url };
}
export function del<R>(url: string): Route<R, undefined> {
  return { method: "DELETE", url };
}

// A small fetcher that uses the route's types
export async function apiFetch<R, B = undefined>(
  route: Route<R, B>,
  body?: B,
  init?: RequestInit
): Promise<R> {
  const isFormData = body instanceof FormData;

  const res = await fetch(route.url, {
    method: route.method,
    credentials: "include",
    headers: {
      // Only set Content-Type for non-FormData requests
      // For FormData, browser sets it automatically with the correct boundary
      ...(body !== undefined && !isFormData
        ? { "content-type": "application/json" }
        : {}),
      ...(init?.headers || {}),
    },
    body:
      body !== undefined
        ? isFormData
          ? body
          : JSON.stringify(body)
        : undefined,
    ...init,
  });

  // Handle 401 Unauthorized - session expired
  if (res.status === 401) {
    // Redirect to login page
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    throw new Error("Session expired. Redirecting to login...");
  }

  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error(json?.error ?? res.statusText);
  return json as R;
}

// small helper for query strings
export function qs(params: Record<string, unknown>) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) s.set(k, String(v));
  }
  return s.toString();
}
